// src/pages/TenantManagement/modals/CreateTenantModal.tsx
import React, { useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Spinner,
  Row,
  Col,
} from 'reactstrap';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';

// Redux
import { createNewTenant, checkCode } from 'slices/tenantManagement/thunk';
import { closeModal } from 'slices/tenantManagement/reducer';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';

// Validation
import { createTenantSchema } from '../validation';

// Types
import { Tenant } from '../types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('tenant_management');

interface CreateTenantModalProps {
  isOpen: boolean;
  toggle: (reload?: boolean) => void;
  tenants: Tenant[]; // For parent selection
  managers: any[]; // List of users for manager selection
}

const CreateTenantModal: React.FC<CreateTenantModalProps> = ({
  isOpen,
  toggle,
  tenants,
  managers,
}) => {
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const { checkingCode, codeAvailable } = useSelector(
    (state: any) => state.TenantManagement
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      code: '',
      name: '',
      type: '1',
      managerId: '',
      description: '',
      address: '',
      phone: '',
      email: '',
      parentId: '',
      status: '1',
    },
    validationSchema: createTenantSchema(false),
        onSubmit: async (values) => {
      try {
        await dispatch(createNewTenant({
          code: values.code,
          name: values.name,
          type: parseInt(values.type),
          managerId: values.managerId ? parseInt(values.managerId) : undefined,
          description: values.description || undefined,
          address: values.address || undefined,
          phone: values.phone || undefined,
          email: values.email || undefined,
          parentId: values.parentId ? parseInt(values.parentId) : undefined,
        })).unwrap();
        
                toast.success(st('tao_don_vi_thanh_cong_message'));
        validation.resetForm();
        toggle(true); // Reload list
      } catch (error: any) {
        toast.error(error || st('co_loi_xay_ra_khi_tao_message'));
      }
    },
  });

  // Real-time code check with debounce
  useEffect(() => {
    const code = validation.values.code;
    if (code && code.length >= 2) {
      const timer = setTimeout(() => {
        dispatch(checkCode({ code }));
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [validation.values.code, dispatch]);

  const handleClose = () => {
    validation.resetForm();
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="lg">
      <ModalHeader toggle={handleClose}>
        <i className="ri-building-line me-2"></i>
        {st('them_don_vi_moi_title')}
      </ModalHeader>
      <Form onSubmit={validation.handleSubmit}>
        <ModalBody>
          <Row>
            {/* Code */}
            <Col md={6}>
              <FormGroup>
                <Label for="code">{st('ma_don_vi_label')}</Label>
                <div className="position-relative">
                  <Input
                    type="text"
                    id="code"
                    name="code"
                    placeholder={st('vd_it_hr_sales_placeholder')}
                    maxLength={20}
                    value={validation.values.code}
                    data-action-code="create-input-code"
                    data-action-name="Mã đơn vị (Thêm mới)"
                    onChange={(e) =>
                      validation.setFieldValue(
                        'code',
                        e.target.value.toUpperCase()
                      )
                    }
                    onBlur={validation.handleBlur}
                    disabled={!PageActionPermission.canEnable(location.pathname, "create-input-code")}
                    invalid={
                      validation.touched.code && validation.errors.code
                        ? true
                        : false
                    }
                  />
                  {/* Chỉ hiển thị icon kiểm tra trùng mã khi định dạng mã đã hợp lệ (không còn lỗi Yup) —
                      tránh vừa báo lỗi định dạng vừa hiện tích xanh "hợp lệ" cùng lúc, gây hiểu lầm. */}
                  {validation.values.code.length >= 2 && !(validation.touched.code && validation.errors.code) && (
                    <div
                      className="position-absolute"
                      style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}
                    >
                      {checkingCode ? (
                        <Spinner size="sm" color="primary" />
                      ) : codeAvailable ? (
                        <i className="ri-checkbox-circle-line text-success fs-5"></i>
                      ) : (
                        <i className="ri-close-circle-line text-danger fs-5"></i>
                      )}
                    </div>
                  )}
                  {validation.touched.code && validation.errors.code ? (
                    <FormFeedback type="invalid">
                      {validation.errors.code}
                    </FormFeedback>
                  ) : null}
                </div>
                <small className="text-muted">
                  {st('chi_chu_hoa_so_va_dau_hint')}
                </small>
              </FormGroup>
            </Col>

                        {/* Type */}
                        <Col md={6}>
                          <FormGroup>
                            <Label for="type">{st('loai_don_vi_label')}</Label>
                            <select
                              id="type"
                              name="type"
                              value={validation.values.type || '1'}
                              data-action-code="create-input-type"
                              data-action-name="Loại đơn vị (Thêm mới)"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              disabled={!PageActionPermission.canEnable(location.pathname, "create-input-type")}
                              className={`form-select ${validation.touched.type && validation.errors.type ? 'is-invalid' : ''}`}
                            >
                              <option value="1">{st('phong_ban_option')}</option>
                              <option value="2">{st('chi_nhanh_option')}</option>
                            </select>
                            {validation.touched.type && validation.errors.type ? (
                              <div className="invalid-feedback d-block">
                                {validation.errors.type}
                              </div>
                            ) : null}
                          </FormGroup>
                        </Col>

            {/* Name */}
            <Col md={12}>
              <FormGroup>
                <Label for="name">{st('ten_don_vi_label')}</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  placeholder={st('nhap_ten_day_du_cua_don_placeholder')}
                  maxLength={200}
                  value={validation.values.name}
                  data-action-code="create-input-name"
                  data-action-name="Tên đơn vị (Thêm mới)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={!PageActionPermission.canEnable(location.pathname, "create-input-name")}
                  invalid={
                    validation.touched.name && validation.errors.name
                      ? true
                      : false
                  }
                />
                {validation.touched.name && validation.errors.name ? (
                  <FormFeedback type="invalid">
                    {validation.errors.name}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </Col>

                        {/* Manager */}
                        <Col md={6}>
                          <FormGroup>
                            <Label for="managerId">{st('quan_ly_text')}</Label>
                            <select
                              id="managerId"
                              name="managerId"
                              value={validation.values.managerId || ''}
                              data-action-code="create-input-manager"
                              data-action-name="Quản lý (Thêm mới)"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              disabled={!PageActionPermission.canEnable(location.pathname, "create-input-manager")}
                              className="form-select"
                            >
                              <option value="">{st('chon_quan_ly_option')}</option>
                              {managers.map((manager) => (
                                <option key={manager.id} value={manager.id}>
                                  {manager.name}
                                </option>
                              ))}
                            </select>
                          </FormGroup>
                        </Col>

                        {/* Parent Tenant */}
                        <Col md={6}>
                          <FormGroup>
                            <Label for="parentId">{st('don_vi_cha_label')}</Label>
                            <select
                              id="parentId"
                              name="parentId"
                              value={validation.values.parentId || ''}
                              data-action-code="create-input-parent"
                              data-action-name="Đơn vị cha (Thêm mới)"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              disabled={!PageActionPermission.canEnable(location.pathname, "create-input-parent")}
                              className="form-select"
                            >
                              <option value="">{st('khong_co_option')}</option>
                              {tenants.map((tenant) => (
                                <option key={tenant.id} value={tenant.id}>
                                  {tenant.name}
                                </option>
                              ))}
                            </select>
                          </FormGroup>
                        </Col>

            {/* Description */}
            <Col md={12}>
              <FormGroup>
                <Label for="description">{st('mo_ta_label')}</Label>
                <Input
                  type="textarea"
                  id="description"
                  name="description"
                  placeholder={st('mo_ta_ve_chuc_nang_va_placeholder')}
                  rows={3}
                  maxLength={500}
                  value={validation.values.description}
                  data-action-code="create-input-description"
                  data-action-name="Mô tả đơn vị (Thêm mới)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={!PageActionPermission.canEnable(location.pathname, "create-input-description")}
                  invalid={
                    validation.touched.description &&
                    validation.errors.description
                      ? true
                      : false
                  }
                />
                {validation.touched.description &&
                validation.errors.description ? (
                  <FormFeedback type="invalid">
                    {validation.errors.description}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </Col>

            {/* Address */}
            <Col md={12}>
              <FormGroup>
                <Label for="address">
                  {st('dia_chi_label')} {validation.values.type === '2' && <span className="text-danger">*</span>}
                </Label>
                <Input
                  type="text"
                  id="address"
                  name="address"
                  placeholder={st('nhap_dia_chi_van_phong_placeholder')}
                  maxLength={255}
                  value={validation.values.address}
                  data-action-code="create-input-address"
                  data-action-name="Địa chỉ đơn vị (Thêm mới)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={!PageActionPermission.canEnable(location.pathname, "create-input-address")}
                  invalid={
                    validation.touched.address && validation.errors.address
                      ? true
                      : false
                  }
                />
                {validation.touched.address && validation.errors.address ? (
                  <FormFeedback type="invalid">
                    {validation.errors.address}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </Col>

            {/* Phone */}
            <Col md={6}>
              <FormGroup>
                <Label for="phone">{st('so_dien_thoai_label')}</Label>
                <Input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder={st('vd_0901234567_placeholder')}
                  maxLength={11}
                  value={validation.values.phone}
                  data-action-code="create-input-phone"
                  data-action-name="Số điện thoại đơn vị (Thêm mới)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={!PageActionPermission.canEnable(location.pathname, "create-input-phone")}
                  invalid={
                    validation.touched.phone && validation.errors.phone
                      ? true
                      : false
                  }
                />
                {validation.touched.phone && validation.errors.phone ? (
                  <FormFeedback type="invalid">
                    {validation.errors.phone}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </Col>

            {/* Email */}
            <Col md={6}>
              <FormGroup>
                <Label for="email">{st('email_label')}</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  placeholder={st('vd_dept_company_com_placeholder')}
                  maxLength={100}
                  value={validation.values.email}
                  data-action-code="create-input-email"
                  data-action-name="Email đơn vị (Thêm mới)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={!PageActionPermission.canEnable(location.pathname, "create-input-email")}
                  invalid={
                    validation.touched.email && validation.errors.email
                      ? true
                      : false
                  }
                />
                {validation.touched.email && validation.errors.email ? (
                  <FormFeedback type="invalid">
                    {validation.errors.email}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </Col>

                        {/* Status */}
                        <Col md={6}>
                          <Label for="status">{st('trang_thai_label')}</Label>
                          <select
                            id="status"
                            name="status"
                            value={validation.values.status || '1'}
                            data-action-code="create-input-status"
                            data-action-name="Trạng thái đơn vị (Thêm mới)"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            disabled={!PageActionPermission.canEnable(location.pathname, "create-input-status")}
                            className="form-select"
                          >
                            <option value="1">{st('hoat_dong_option')}</option>
                            <option value="0">{st('ngung_hoat_dong_option')}</option>
                          </select>
                        </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={handleClose}>
            <i className="ri-close-line me-1"></i>
            {st('huy_bo_button')}
          </Button>
          {PageActionPermission.canDisplay(location.pathname, "button-create-submit") && (
          <Button
            color="primary"
            type="submit"
            data-action-code="button-create-submit"
            data-action-name="Tạo đơn vị mới"
          >
            <i className="ri-save-line me-1"></i>
            {st('tao_moi_button')}
          </Button>
          )}
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CreateTenantModal;